<template>
    <div class="flex flex-row items-center gap-3 min-w-0">
        <div
            class="ring-inset rounded-sm size-10 shrink-0 flex items-center justify-center p-0.5 overflow-hidden"
            :class="logoBoxClasses"
        >
            <img
                v-if="props.logoUrl && !hasLoadingImageFailed"
                :src="props.logoUrl"
                :alt="props.logoAlt"
                class="max-h-full max-w-full"
                @error="onImageError"
            />

            <HelpButton v-else-if="hasLoadingImageFailed" status="Error" :text="$t('loading-logo-failed')" />

            <p v-else class="text-xxs text-gray-500 text-center hyphens-auto leading-tight" aria-hidden="true">
                {{ $t('no-logo') }}
            </p>
        </div>

        <div class="flex flex-col justify-around self-stretch min-w-0">
            <Tooltip v-if="props.subtitle" class="max-w-full" :is-disabled="!isSubtitleTruncated">
                <template #trigger="tooltipProps">
                    <p
                        ref="subtitleElement"
                        class="text-xs leading-none text-gray-500 min-w-0 overflow-x-clip text-ellipsis whitespace-nowrap rounded-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700"
                        :class="{ 'cursor-help': isSubtitleTruncated }"
                        :aria-describedby="isSubtitleTruncated ? tooltipProps['aria-labelledby'] : undefined"
                        :tabindex="isSubtitleTruncated ? 0 : undefined"
                    >
                        {{ props.subtitle }}
                    </p>
                </template>

                <template #text>
                    <span class="block max-w-96">{{ props.subtitle }}</span>
                </template>
            </Tooltip>

            <Tooltip class="max-w-full" :is-disabled="!isTitleTruncated">
                <template #trigger="tooltipProps">
                    <h1
                        ref="titleElement"
                        class="text-lg leading-none font-semibold text-gray-700 min-w-0 overflow-x-clip text-ellipsis whitespace-nowrap rounded-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700"
                        :class="{ 'cursor-help': isTitleTruncated }"
                        :aria-describedby="isTitleTruncated ? tooltipProps['aria-labelledby'] : undefined"
                        :tabindex="isTitleTruncated ? 0 : undefined"
                    >
                        {{ props.title }}
                    </h1>
                </template>

                <template #text>
                    <span class="block max-w-96">{{ props.title }}</span>
                </template>
            </Tooltip>
        </div>
    </div>
</template>

<script setup lang="ts">
import HelpButton from '@/components/HelpButton/HelpButton.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { useImageError } from '@/utils/useImageError';
import { useElementOverflow } from '@vueuse/core';
import { computed, useTemplateRef } from 'vue';

type TitleWithLogoProps = {
    title: string;
    subtitle?: string;
    logoUrl?: string;
    logoAlt?: string;
};

const props = defineProps<TitleWithLogoProps>();

const titleElement = useTemplateRef('titleElement');
const subtitleElement = useTemplateRef('subtitleElement');

const { isXOverflowed: isTitleTruncated } = useElementOverflow(titleElement, { observeMutation: true });
const { isXOverflowed: isSubtitleTruncated } = useElementOverflow(subtitleElement, { observeMutation: true });

const { hasLoadingImageFailed, onImageError } = useImageError(false);

const logoBoxClasses = computed(() => {
    if (hasLoadingImageFailed.value) {
        return 'ring-2 ring-red-700 bg-red-200';
    }
    return props.logoUrl ? 'ring-1 ring-gray-200 bg-gray-50' : 'ring-2 ring-gray-200 bg-gray-50';
});
</script>
